import { useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import api from 'api';
import { getBlockNameByType } from 'lib/blocks';
import {
  getDefaultInterfaceLanguage,
  getCourseLanguageVersionOptions
} from 'lib/courses';
import { ModalButtonWithForm } from 'components/common';
import {
  Checkbox,
  FormGroup,
  RadioGroup,
  Select
} from 'components/common/form';
import { localeNames } from 'shared-locales';
import { CourseUnsavedChaptersAlert } from '.';

import type { FormApi } from 'informed';
import type { Block, Course, Locales } from 'shared-types';

interface FormValues {
  export: 'course' | 'chapter';
  chapter?: string;
  courseContent: 'all' | 'selected';
  courseLanguage: Course['language'];
  contentTypes?: Record<Block['type'], boolean>;
  interfaceLanguage: Locales;
}

const CourseDownloadPdf = () => {
  const {
    language: appLanguage,
    messages,
    pageState: { course },
    serverConfig: { supportedLanguages }
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const [error, setError] = useState<string | null>(null);

  const courseLanguages = getCourseLanguageVersionOptions({
    course
  });
  const hasLanguageVersions = Object.keys(courseLanguages).length > 1;

  const blocks = getBlockNameByType({ messages });

  const initialValues = {
    export: 'course',
    chapter: course.chapters.length ? course.chapters[0].slug : null,
    courseLanguage: course.language,
    interfaceLanguage: getDefaultInterfaceLanguage({
      appLanguage,
      courseLanguage: course.language,
      supportedLanguages
    })
  };

  const syncInterfaceLanguage = (courseLanguage: string) => {
    if (formApi.current.getTouched('interfaceLanguage')) {
      return; // don't auto-sync interface language if the user has manually selected it
    }
    const newInterfaceDefault = getDefaultInterfaceLanguage({
      appLanguage,
      courseLanguage: courseLanguage,
      supportedLanguages
    });
    formApi.current.setValue('interfaceLanguage', newInterfaceDefault);
  };

  const submit = async (values: FormValues) => {
    const query: string[] = [];
    if (values.export === 'chapter') {
      query.push(`chapterSlug=${values.chapter}`);
    }
    if (values.courseContent === 'selected' && values.contentTypes) {
      query.push(
        `contentTypes=${Object.keys(values.contentTypes)
          ?.filter((key) => values.contentTypes[key])
          .join(',')}`
      );
    }

    const courseLanguage = values.courseLanguage || course.language;

    const url =
      `/admin/courses/${course.id}/pdf-export` +
      `?courseLanguage=${courseLanguage}&interfaceLanguage=${values.interfaceLanguage}` +
      (query.length ? `&${query.join('&')}` : '');

    const response = await api.get(url, { responseType: 'blob' });

    const filename =
      `${course.slug}` +
      `${values.export === 'chapter' ? `-${values.chapter}` : ''}` +
      `-${courseLanguage}.pdf`;

    const blob = new Blob([response.data]);

    // download file by simulating a link click
    const fileUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return true;
  };

  const validate = (values: FormValues) => {
    if (values.courseContent === 'selected') {
      const hasAtLeastOneContentType = Object.values(
        values.contentTypes || {}
      ).some(Boolean);
      if (!hasAtLeastOneContentType) {
        setError(messages.courseExportAsPdfContentTypesRequired);
        return false;
      }
    }

    setError(null);
    return true;
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={!course.chapters.length}
      buttonIcon="file-pdf"
      buttonText={messages.courseExportAsPdf}
      initialValues={initialValues}
      modalActionText={messages.courseExportAsPdf}
      modalTitle={messages.courseExportAsPdf}
      modalWidth="medium"
      onSubmit={submit}
      pristineIsValid={true}
      preventSubmitOnEnterInFormElement={true}
      setFormApi={(form) => (formApi.current = form)}
      validateBeforeSubmit={validate}
    >
      {({ values }: { values: FormValues }) => (
        <div className="form">
          <CourseUnsavedChaptersAlert />

          <div className="info">{messages.summaryExportInfo}</div>

          <RadioGroup
            field="export"
            options={[
              {
                id: 'export-course',
                label: messages.summaryExportCourse,
                value: 'course'
              },
              {
                id: 'export-chapter',
                label: messages.summaryExportChapter,
                value: 'chapter'
              }
            ]}
            spacing="8px"
          />
          <div
            className={`chapters ${
              values.export === 'chapter' ? 'visible' : ''
            }`}
          >
            <RadioGroup
              field="chapter"
              options={course.chapters.map((chapter) => ({
                id: `export-chapter-id-${chapter.id}`,
                label: `${chapter.number}. ${chapter.title}`,
                value: chapter.slug
              }))}
              spacing="8px"
            />
          </div>

          <FormGroup label={messages.summaryExportCourseContent}>
            <RadioGroup
              field="courseContent"
              initialValue="all"
              options={[
                {
                  id: 'content-types-all',
                  label: messages.commonAll,
                  value: 'all'
                },
                {
                  id: 'content-types-selected',
                  label: messages.commonSelect,
                  value: 'selected'
                }
              ]}
              spacing="8px"
            />

            {values.courseContent === 'selected' ? (
              <FormGroup error={error} label={messages.commonContentTypes}>
                <div className="content-types-grid">
                  {Object.keys(blocks)
                    .sort((a, b) => blocks[a].localeCompare(blocks[b]))
                    .map((block) => (
                      <div key={block}>
                        <Checkbox
                          field={`contentTypes.${block}`}
                          id={`content-types-${block}`}
                          label={blocks[block]}
                        />
                      </div>
                    ))}
                </div>
              </FormGroup>
            ) : null}
          </FormGroup>

          {hasLanguageVersions && (
            <FormGroup label={messages.courseLanguage}>
              <Select
                field="courseLanguage"
                id="course-language"
                onChange={syncInterfaceLanguage}
                options={Object.keys(courseLanguages).map((language) => ({
                  label: courseLanguages[language].capitalizedName,
                  value: language
                }))}
                style={{ maxWidth: 200 }}
              />
            </FormGroup>
          )}

          <FormGroup label={messages.interfaceLanguage}>
            <Select
              field="interfaceLanguage"
              id="interface-language"
              options={supportedLanguages.map((locale) => ({
                label: localeNames[locale],
                value: locale
              }))}
              style={{ maxWidth: 200 }}
            />
          </FormGroup>

          <style jsx>{`
            .info {
              margin-bottom: 15px;
            }
            .chapters {
              display: none;
              margin-top: 8px;
              padding-left: 25px;
            }
            .chapters.visible {
              display: block;
            }
            .content-types-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CourseDownloadPdf;
